/**
 * O selo de certificação de um ambiente, onde o tenant é identificado.
 *
 * Spec: `docs/superpowers/specs/2026-09-28-selo-de-certificacao-design.md` §5.1.
 *
 * **É TEXTO, não ícone.** Um escudo verde não diz o que foi certificado, por quem, nem até
 * quando — e é exatamente isso que um selo precisa dizer. Ícone aqui seria o `has_pci` de
 * novo, com outra aparência.
 *
 * **Vencido não esconde e não fica vermelho de alarme**: passa a dizer `vencido em`, porque
 * esconder é pior que mostrar vencido.
 *
 * Os rótulos vivem em `ROTULOS` e em nenhum outro lugar: são decisão do proprietário (portão
 * P1 do plano), e trocá-los tem de ser uma linha.
 */
import { Badge, Text, Tooltip } from '@mantine/core'
import { FRAMEWORK_LABELS, type Selo } from '@/api/certificacoes'

/** Rótulos CONFIRMADOS pelo proprietário em 29/09/2026 (portão P1, fechado).
 *
 *  "Auditável" sozinho foi descartado na spec: todo ambiente é auditável, então o selo não
 *  afirmaria nada contestável. O de transição nomeia o ambiente de destino, que vem do
 *  vínculo `escopo` do mesmo certificado — não há coluna para manter.
 */
export const ROTULOS = {
  interno: 'Controles auditados · fora do escopo',
  transicao: 'Fora do escopo · em transição para',
  transicaoSemDestino: 'Fora do escopo · em transição para o ambiente certificado',
} as const

export function dataBr(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [a, m, d] = String(iso).slice(0, 10).split('-')
  return d && m && a ? `${d}/${m}/${a}` : String(iso)
}

/** Hoje em ISO, para comparar com `data_virada` sem fuso no meio.
 *
 *  Comparação de string `YYYY-MM-DD` é correta e é o que se quer aqui: a virada é um fato de
 *  CALENDÁRIO, não um instante. `new Date(iso)` interpretaria a data como meia-noite UTC e, em
 *  `America/Manaus` (−04:00), o dia da virada apareceria como já vencido desde as 20h do dia
 *  anterior.
 */
export function hojeISO(agora: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${agora.getFullYear()}-${p(agora.getMonth() + 1)}-${p(agora.getDate())}`
}

/** O texto do selo, derivado do vínculo. Exportado porque é o que o teste decide. */
export function textoDoSelo(selo: Selo): string {
  const fw = FRAMEWORK_LABELS[selo.framework] ?? selo.framework
  const versao = selo.versao_framework ? ` ${selo.versao_framework}` : ''
  if (selo.estado === 'revogado') return `${fw} · certificado revogado`
  if (selo.estado === 'vencido') return `${fw} · vencido em ${dataBr(selo.vence_em)}`
  if (selo.tipo === 'escopo') return `${fw}${versao} · vence ${dataBr(selo.vence_em)}`
  if (selo.tipo === 'transicao') {
    const base = selo.destino
      ? `${ROTULOS.transicao} ${selo.destino}`
      : ROTULOS.transicaoSemDestino
    // Derivado de `data_virada`, e NUNCA de comparar `revisar_em` com `vence_em` — era assim
    // até 29/09/2026, e por isso qualquer data de revisão diferente do vencimento fazia o
    // console AFIRMAR uma virada prevista que ninguém decidiu (spec §12.2).
    if (!selo.data_virada) {
      return `${base} · sem data de virada, revisar até ${dataBr(selo.revisar_em)}`
    }
    // O caso do passado não é zelo: o vínculo de transição só termina por DELETE, nada o apaga
    // por data. Então o dia depois da virada planejada é o estado MAIS provável — a data chega,
    // o ambiente continua, ninguém mexeu na tela — e dizer "prevista" ali afirma o que o
    // calendário já contradisse.
    return selo.data_virada < hojeISO()
      ? `${base} · virada vencida em ${dataBr(selo.data_virada)}`
      : `${base} · virada prevista para ${dataBr(selo.data_virada)}`
  }
  return ROTULOS.interno
}

/** Curto, para caber na linha da tabela. O texto inteiro fica no tooltip. */
export function textoCurto(selo: Selo): string {
  const fw = FRAMEWORK_LABELS[selo.framework] ?? selo.framework
  if (selo.estado === 'revogado') return `${fw} revogado`
  if (selo.estado === 'vencido') return `${fw} vencido`
  if (selo.tipo === 'escopo') return `${fw} até ${dataBr(selo.vence_em).slice(3)}`
  if (selo.tipo === 'transicao') return 'em transição'
  return 'controles auditados'
}

/** Cor por TIPO, e o estado vence o tipo: vencido e revogado saem do verde.
 *
 *  `gray` no vencido em vez de `red`: o selo informa, não alarma — e vermelho numa linha de
 *  tabela lê como incidente em curso.
 */
function cor(selo: Selo): string {
  if (selo.estado === 'revogado' || selo.estado === 'vencido') return 'gray'
  if (selo.tipo === 'escopo') return 'inovareTeal'
  if (selo.tipo === 'transicao') return 'orange'
  return 'blue'
}

interface Props {
  selo: Selo | undefined
  /** `true` na tabela (chip curto); `false` no cabeçalho do tenant (texto inteiro). */
  curto?: boolean
  tenantId?: number
}

export function SeloDoAmbiente({ selo, curto = true, tenantId }: Props) {
  // Ambiente sem vínculo não recebe selo vazio nem "sem certificação": ausência é a
  // resposta, e é o caso dos outros 90 tenants.
  if (!selo) return null
  const completo = textoDoSelo(selo)
  const testId = tenantId ? `selo-certificacao-${tenantId}` : 'selo-certificacao'
  if (!curto) {
    return (
      <Text size="sm" c="var(--ai-text-secondary)" data-testid={testId}>
        {completo}
        {selo.motivo ? ` — ${selo.motivo}` : ''}
      </Text>
    )
  }
  return (
    <Tooltip label={completo} multiline w={320} withArrow>
      <Badge size="sm" variant="light" color={cor(selo)} data-testid={testId}>
        {textoCurto(selo)}
      </Badge>
    </Tooltip>
  )
}
